// ─── Currency formatting helpers (presentational) ────────────────────────────
// Thin, currency-aware wrappers over the single source of truth in
// `@bbpos/utils`. `packages/ui` components are presentational: they take a
// `currency?: CurrencyCode` prop (defaulting to `'INR'`) and format through
// these helpers — they never read tenant context/services. The INR path is
// byte-identical to the prior inline `₹${(paise / 100).toLocaleString(...)}`
// implementations (golden-tested), while non-INR tenants get correct subunit
// math from `@bbpos/utils` (OMR/BHD/KWD are 3-decimal currencies).

import { amountToMajor, formatAmount, getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';

/**
 * Derive a currency's display symbol from `@bbpos/utils` without duplicating the
 * symbol table here. `formatAmount` is defined as `symbol + amountToMajor`, so
 * stripping the formatted-amount suffix yields exactly the symbol the rest of
 * the codebase uses (`₹` for INR, ISO/script fallbacks for the GCC set).
 */
export function currencySymbol(currency: CurrencyCode = 'INR'): string {
  const major = amountToMajor(0, currency);
  return formatAmount(0, currency).slice(0, -major.length);
}

/**
 * Format an amount in the smallest unit with 2/3-decimal precision and the
 * currency symbol. Byte-identical to the legacy
 * `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
 * for INR (delegates straight to `@bbpos/utils#formatAmount`).
 */
export function formatMoney(amount: number, currency: CurrencyCode = 'INR'): string {
  return formatAmount(amount, currency);
}

/**
 * Format an amount in the smallest unit with caller-controlled fraction digits
 * (compact / no-decimal / no-grouping displays). The subunit divisor is sourced
 * from `@bbpos/utils` rather than a hardcoded `/ 100`, so 3-decimal currencies
 * render correctly; INR + the supplied `options` reproduce the prior inline
 * `toLocaleString('en-IN', options)` output byte-for-byte.
 */
export function formatMoneyParts(
  amount: number,
  currency: CurrencyCode = 'INR',
  options?: Intl.NumberFormatOptions,
): string {
  const major = amount / getSubunitMultiplier(currency);
  return `${currencySymbol(currency)}${major.toLocaleString('en-IN', options)}`;
}
